import type { UnknownAction } from '@reduxjs/toolkit';

import { resourceUndoUpserters, resourceUpserters } from 'app-components/flux/core';
import { hasAsyncDispatch } from 'app-components/utils/redux-store/asyncDispatchMiddleware';

import * as actions from './goalsActions';
import { transformGoalForOptimisticUpdate } from './goalsTransformers';
import {
  GOALS_REDUCER_KEY,
  type Goal,
  type GoalProps,
  type GoalRecord,
  type GoalsStore,
  type GoalsStoreProps,
  mkGoal,
  mkGoalsStore,
} from './goalsTypes';

const toGoalRecord = (goal: Goal | GoalRecord): GoalRecord =>
  'merge' in goal && typeof goal.merge === 'function' ? goal : mkGoal(goal.type ?? 'SAVE_FOR_OTHER', goal);

function goalsReducer(state: GoalsStore = mkGoalsStore(), action: UnknownAction): GoalsStore {
  switch (true) {
    case actions.createGoal.match(action):
      return resourceUpserters.upsertResource<GoalProps, GoalsStoreProps>(state, toGoalRecord(action.payload));

    case actions.createGoalSuccess.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo<GoalProps, GoalsStoreProps>(
        state,
        { ...action, payload: toGoalRecord(action.payload) },
        actions.deleteGoal,
      );

    case actions.updateGoal.match(action): {
      // prune EPOCH date from optimistic update (only needed to clear QCS values)
      // Do not mutate the action payload so sagas still send EPOCH_ISO to QCS.
      const optimisticAction = {
        ...action,
        payload: transformGoalForOptimisticUpdate(toGoalRecord(action.payload)),
      };
      return resourceUndoUpserters.upsertResourceSetupUndo<GoalProps, GoalsStoreProps>(
        state,
        optimisticAction,
        actions.updateGoal,
      );
    }

    case actions.updateGoalSuccess.match(action):
      return resourceUpserters.upsertResource<GoalProps, GoalsStoreProps>(state, toGoalRecord(action.payload));

    case actions.deleteGoal.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo<GoalProps, GoalsStoreProps>(
        state,
        { ...action, payload: toGoalRecord(action.payload) },
        actions.createGoal,
      );

    case actions.deleteGoalSuccess.match(action):
      return resourceUpserters.upsertResource<GoalProps, GoalsStoreProps>(state, toGoalRecord(action.payload));

    case actions.getGoals.match(action):
      return state.set('isLoading', true);

    case actions.getGoalsSuccess.match(action): {
      return resourceUpserters.upsertResources<GoalProps, GoalsStoreProps>(state, action.payload);
    }

    case actions.getGoalsFailure.match(action):
      return resourceUpserters.completeWithError<GoalProps, GoalsStoreProps>(state, action.payload);

    case actions.createGoalFailure.match(action):
    case actions.updateGoalFailure.match(action):
    case actions.deleteGoalFailure.match(action): {
      if (hasAsyncDispatch(action)) {
        action.asyncDispatch(actions.getGoals());
      }
      return resourceUpserters.resetResources<GoalProps, GoalsStoreProps>(state, action.payload);
    }

    case actions.updateChangedGoals.match(action):
      return resourceUpserters.upsertResources<GoalProps, GoalsStoreProps>(state, { resources: action.payload });

    default:
      return state;
  }
}

export const REDUCER_KEY = GOALS_REDUCER_KEY;
export default goalsReducer;
